/* eslint-disable @typescript-eslint/no-explicit-any */
import type { IMatchOfficial } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";

export type FormResult = "W" | "L" | "T" | "NR";

export const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

const nullish = (s?: string | null) => {
  const t = (s ?? "").trim().toLowerCase();
  return !t || t === "null" || t === "undefined";
};

export const fullName = (p?: { playerFirstName?: string | null; playerLastName?: string | null } | null) =>
  [p?.playerFirstName, p?.playerLastName].filter((x) => !nullish(x)).join(" ").trim();

export const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";

export const officialName = (o?: IMatchOfficial | string | null): string => {
  if (!o) return "";
  if (typeof o === "string") return nullish(o) ? "" : o.trim();
  return fullName(o) || (nullish(o.name) ? "" : (o.name ?? "").trim());
};

export const humanize = (v?: string | null) =>
  nullish(v) ? "" : (v as string).split("_").map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");

export const resultFor = (match: any, teamName: string): FormResult => {
  const r = norm(match?.matchResult);
  if (!r || r === "-") return "NR";
  if (r.includes("no result") || r.includes("abandon")) return "NR";
  if (r.includes("tie")) return "T";
  return r.includes(norm(teamName)) ? "W" : "L";
};

export const matchTime = (m: any): number => new Date(m?.startDate || m?.dateTime || 0).getTime();

/** score of a team's innings in a (past) match, e.g. {text: "142/6", overs: "20"} */
export const teamInnings = (match: any, teamName: string): { text: string; overs: string } | null => {
  const inn = (match?.inningScorecardList ?? []).find((i: any) => norm(i?.battingTeamName) === norm(teamName));
  if (!inn) return null;
  const text = inn.wicket >= 10 ? `${inn.runs}` : `${inn.runs}/${inn.wicket}`;
  const overs = inn.ball ? `${inn.over}.${inn.ball}` : `${inn.over}`;
  return { text, overs };
};

export type Countdown = { d: number; h: number; m: number; started: boolean };
export const countdownTo = (startMs: number, nowMs: number): Countdown => {
  const s = Math.max(0, Math.floor((startMs - nowMs) / 1000));
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), started: startMs <= nowMs };
};

export const whenLabel = (start: Date, now: Date): string => {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(start) - day(now)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

export const timeLabel = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
export const longDate = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export const roleTag = (role?: string | null): string => {
  const r = (role ?? "").toUpperCase();
  if (r === "CAPTAIN") return "C";
  if (r === "VICE_CAPTAIN") return "VC";
  return "";
};

export const pad2 = (n: number) => String(n).padStart(2, "0");
